<template lang="pug">
  .vimeo-player.trans-opacity-quick(:class="{'opacity-0': invisible, 'absolute inset-0 h-full w-full': bgSize, 'vimeo-player--active': playing !== null}")
    //- frame
    .vimeo-player__frame.overflow-hidden(ref="vimeo", :class="{'relative': !bgSize, 'pb-ar-16x9': !bgSize && !loaded, 'absolute': bgSize, 'vimeo-player__frame--blurred': blur}", :style="!bgSize && ratio && {paddingBottom: `calc(100% * ${ratio})`}")
      //- <iframe> will be injected
      //- controls
      .vimeo-player__controls.absolute.inset-0.flex.items-center.justify-center.z-10.trans-opacity-quick(v-if="controls", :class="{'opacity-0': blur}")
        //- play/pause
        button(v-show="!playing", @click="playBtn", :disabled="blur", aria-label="Play")
          img.select-none(src="@/assets/icons/play-btn.svg")
        button(v-show="playing === true", @click="pause", aria-label="Pause")
          img.select-none(src="@/assets/icons/pause-btn.svg")
        //- mute, progress
        .absolute.w-full.bottom-0.left-0.flex.justify-center.pb-18(v-if="playing !== null")
          .w-11x18.flex.items-center.justify-between
            .p-4
              button(v-show="muted", @click="unmute", aria-label="Unmute")
                img.select-none(src="@/assets/icons/is-muted.svg")
              button(v-show="!muted", @click="mute", aria-label="Mute")
                img.select-none(src="@/assets/icons/audio.svg")
            progress.flex-1.h-4.cursor-pointer(max="100", :value="prgrss", @click="onProgressClick")
</template>

<script>
import Player from '@vimeo/player'
import { mapState } from 'vuex'
export default {
  name: 'VimeoPlayer',
  props: ['vimeoURL', 'bgSize', 'clip', 'controls', 'progress', 'visible'],
  data () {
    return {
      player: null,
      loaded: false,
      invisible: true,
      blur: null,
      elW: 0,
      elH: 0,
      videoW: 0,
      videoH: 0,
      iframe: null,
      playing: null,
      prgrss: 0,
      muted: true
    }
  },
  computed: {
    ...mapState({ globalBlur: 'blur' }),
    elRatio () {
      return this.elH / this.elW
    },
    ratio () {
      return this.videoH / this.videoW
    }
  },
  methods: {
    init () {
      if (!this.vimeoURL) return console.warn('No vimeo URL')
      const options = {
        url: this.vimeoURL,
        background: true,
        loop: true
      }
      // slow connection or data saver: a background clip does not need a full-size rendition (H1)
      try {
        const conn = navigator.connection
        if (conn && (conn.saveData || ['slow-2g', '2g', '3g'].includes(conn.effectiveType))) {
          options.quality = '540p'
        }
      } catch (e) { /* connection info unavailable */ }
      if (this.bgSize === 'cover') {
        options.maxheight = this.elH * 0.8
      } else {
        options.maxwidth = this.elW
      }
      // create player
      this.player = new Player(this.$refs.vimeo, options)
      // a failed embed (bad URL, private video, blocked network) is reported instead of hanging on the poster (B1)
      this.player.ready().catch(this.onError)
      this.player.on('error', this.onError)
      // events
      this.player.on('loaded', () => {
        this.loaded = true
        this.setClip()
        this.$emit('loaded')
        this.resizeIframe() // resize and reveal
      })
      this.player.on('timeupdate', this.onTimeUpdate)
      this.player.on('timeupdate', this.onFirstPlay) // binding to @play is inconsistent !
    },
    play () {
      return this.player && this.player.getPaused()
        .then(paused => paused && this.player.play())
        .catch(() => {})
    },
    pause () {
      return this.player && this.player.getPaused()
        .then(paused => !paused && this.player.pause())
        .catch(() => {})
    },
    measure () {
      this.elW = this.$el.offsetWidth
      this.elH = this.$el.offsetHeight
    },
    resizeIframe () {
      this.iframe = this.iframe || this.$el.querySelector('iframe')
      const iframe = this.iframe
      const size = this.bgSize
      if (!iframe) return
      // get aspect ratio
      if (!this.videoW) {
        this.videoW = iframe.getAttribute('width')
        this.videoH = iframe.getAttribute('height')
      }
      // resize (if background)
      if (size) {
        const isWider = this.ratio <= this.elRatio // video wider than frame
        const scaleByHt = (size === 'cover' && isWider) || (size === 'contain' && !isWider)
        // scale the other dimension
        let sideB = this.elH * this.videoW / this.videoH // width (landscape video)
        sideB = this.ratio >= 1 ? this.elW * this.videoH / this.videoW : sideB // height (portrait video)
        if (scaleByHt) {
          iframe.setAttribute('height', this.elH)
          iframe.setAttribute('width', parseInt(sideB))
        } else {
          iframe.setAttribute('height', parseInt(sideB))
          iframe.setAttribute('width', this.elW)
        }
      }
      this.invisible = false
    },
    setClip () {
      // jump to clip start (if set)
      // this.clip = [start, end]
      if (this.clip && this.clip[0] !== undefined && this.clip[0] !== null && this.clip[1]) {
        this.player.setCurrentTime(this.clip[0]).catch(() => {})
      }
    },
    onFirstPlay () {
      /**
       * Fires when the video has visible imagery.
       * - bound to 'timeupdate' because the 'play' event fires inconsistently
       * - a preloaded (not yet visible) slide keeps listening and reports once it is actually shown
       **/
      if (this.visible === false) return
      this.player.off('timeupdate', this.onFirstPlay)
      this.blur = false
      this.$Progress.finish()
      this.$emit('firstplay')
    },
    onError (err) {
      console.warn('Vimeo player error', this.vimeoURL, err && err.name ? err.name : err)
      this.$emit('error', err)
    },
    onTimeUpdate (time) {
      this.prgrss = time.percent * 100
      // if in clip mode - loop back to clip start on clip end
      if (this.playing === null && this.clip) {
        const end = this.clip[1]
        return end && time.seconds >= end && this.player.setCurrentTime(this.clip[0] || 0).catch(() => {})
      }
    },
    playBtn () {
      const plyr = this.player
      // disable clip mode, set time to 0
      if (this.playing === null) {
        this.playing = true
        this.$Progress.start()
        return plyr.setCurrentTime(0).then(time => {
          this.$Progress.finish()
          plyr.on('play', () => { this.playing = true })
          plyr.on('pause', () => { this.playing = false })
        }).catch(() => {})
      }
      return plyr.play().catch(() => {})
    },
    onProgressClick (e) {
      const pct = e.offsetX / e.target.offsetWidth
      this.player.getDuration().then((dur) => {
        this.$Progress.start()
        this.player.setCurrentTime(pct * dur).then(time => {
          this.$Progress.finish()
        })
      }).catch(err => console.error(err))
    },
    mute () {
      return this.setMuted(true)
    },
    unmute () {
      return this.setMuted(false)
    },
    // Selected Works sound toggle (B4): muted by default, the volume control is Vimeo's own setVolume
    setMuted (muted) {
      if (!this.player) return
      return this.player.setVolume(muted ? 0 : 1)
        .then(() => { this.muted = muted })
        .catch(() => {})
    },
    onNewWindowWidth () {
      this.measure()
      this.resizeIframe()
    }
  },
  created () {
    if (this.progress) this.$Progress.start()
    this.blur = this.globalBlur || this.visible
  },
  mounted () {
    this.measure()
    this.init()
    this.$store.state.bus.$on('newWindowWidth', this.onNewWindowWidth)
  },
  beforeDestroy () {
    this.$store.state.bus.$off('newWindowWidth', this.onNewWindowWidth)
    // release the embed so a slide that rotated out of view stops streaming (H1)
    if (this.player) {
      this.player.off('timeupdate', this.onTimeUpdate)
      this.player.off('timeupdate', this.onFirstPlay)
      this.player.off('error', this.onError)
      this.player.destroy().catch(() => {})
      this.player = null
    }
  }
}
</script>

<style>
.vimeo-player__frame{
  &.absolute{
    top:50%; left:50%;
    transform: translate(-50%, -50%);
  }
  & iframe {
    transition: filter 600ms;
    pointer-events: none !important;
  }
  &.vimeo-player__frame--blurred iframe{
    filter:blur(6px);
  }
  &.relative iframe{
    position: absolute;
    top:0; left:0;
    width:100% !important;
    height:100% !important;
  }
}

/* show/hide controls */
.vimeo-player--active{
  & .vimeo-player__controls{opacity:0;}
  & .vimeo-player__frame:hover .vimeo-player__controls{opacity:1;}
}

/* style video progress bar */
.vimeo-player progress{
  appearance:none;
  &[value]::-webkit-progress-bar{
    background: rgba(0,0,0,.5);
  }
  &[value]::-webkit-progress-value{
    background:white;
  }
}

/* blur iframe on rt loading */
.blur-media .vimeo-player {
  & iframe {
    filter:blur(6px);
  }
}
</style>
